import { type FC, type ReactNode, useState, useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';

/**
 * Dropdown component props
 */
export interface DropdownProps {
  /** Trigger element (button, icon, etc.) */
  trigger: ReactNode;
  /** Dropdown content */
  children: ReactNode;
  /** Alignment of dropdown relative to trigger */
  align?: 'left' | 'right';
  /** Additional className for dropdown container */
  className?: string;
}

/**
 * Dropdown - Reusable dropdown menu component
 *
 * Features:
 * - Click outside to close
 * - Keyboard accessible (Escape to close)
 * - Configurable alignment
 * - Portal-free (uses absolute positioning)
 */
export const Dropdown: FC<DropdownProps> = ({
  trigger,
  children,
  align = 'right',
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {trigger}
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute top-full mt-2 z-50',
            'min-w-[200px]',
            'bg-bg-secondary border border-border-default rounded-default shadow-lg',
            'py-2',
            align === 'right' ? 'right-0' : 'left-0',
            className
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * DropdownItem - Individual item in a dropdown menu
 */
export interface DropdownItemProps {
  /** Item content */
  children: ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Icon to display before text */
  icon?: ReactNode;
  /** Variant style */
  variant?: 'default' | 'danger';
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

export const DropdownItem: FC<DropdownItemProps> = ({
  children,
  onClick,
  icon,
  variant = 'default',
  disabled = false,
  className,
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full px-4 py-2',
        'flex items-center gap-3',
        'text-left text-sm',
        'transition-colors duration-fast',
        variant === 'default' && [
          'text-text-primary',
          'hover:bg-bg-hover hover:text-accent-primary',
        ],
        variant === 'danger' && [
          'text-semantic-error',
          'hover:bg-semantic-error/10',
        ],
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </button>
  );
};

/**
 * DropdownDivider - Visual separator in dropdown menu
 */
export const DropdownDivider: FC = () => {
  return <div className="my-2 border-t border-border-subtle" />;
};
